/**
 * 🎮 USE GAMIFICATION HOOK — Dados de gamificação com cache compartilhado (React Query)
 */

import { useQuery } from '@tanstack/react-query';
import { GamificationService, type UserPoints, type LeaderboardEntry } from '../services/gamificationService';
import { loadUncreditedAuthorityScore } from '../services/authorityScore';
import { supabase } from '../lib/supabaseClient';
import { useAuth } from '../context/AuthContext';
import { useWorkspace } from '../contexts/WorkspaceContext';
import type { AuthorityLevel } from '../components/UserAuthorityBadge';

export const gamificationQueryKey = (userId: string, organizationId: string) =>
  ['gamification', 'profile', userId, organizationId] as const;

function calculateLevelFromPoints(points: number): AuthorityLevel {
  if (points >= 15000) return 'black_belt';
  if (points >= 5000) return 'gold';
  if (points >= 1000) return 'silver';
  return 'bronze';
}

function deriveEffectiveTotalPoints(raw: UserPoints): number {
  const declared = Number(raw.total_points ?? 0);
  const composite =
    Number(raw.achievement_points ?? 0) +
    Number(raw.discipline_points ?? 0) +
    Number(raw.flow_points ?? 0);

  const safeDeclared = Number.isFinite(declared) ? declared : 0;
  const safeComposite = Number.isFinite(composite) ? composite : 0;
  return Math.max(safeDeclared, safeComposite);
}

function normalizeUserPoints(raw: UserPoints | null, userId: string, organizationId: string): UserPoints | null {
  if (!raw) return null;

  const totalPoints = deriveEffectiveTotalPoints(raw);
  const storedLevel = raw.authority_level as AuthorityLevel | undefined;
  const calculatedLevel = calculateLevelFromPoints(totalPoints);
  const authorityLevel =
    storedLevel && totalPoints === 0 ? storedLevel : calculatedLevel;

  return {
    ...raw,
    user_id: raw.user_id ?? userId,
    organization_id: raw.organization_id ?? organizationId,
    total_points: totalPoints,
    discipline_points: Number(raw.discipline_points ?? 0),
    achievement_points: Number(raw.achievement_points ?? 0),
    team_points: Number(raw.team_points ?? 0),
    kaizens_completed: Number(raw.kaizens_completed ?? 0),
    projects_led: Number(raw.projects_led ?? 0),
    team_collaborations: Number(raw.team_collaborations ?? 0),
    recovery_plans_opened: Number(raw.recovery_plans_opened ?? 0),
    current_multiplier: Number(raw.current_multiplier ?? 1),
    authority_level: authorityLevel,
  };
}

interface GamificationQueryResult {
  userPoints: UserPoints | null;
  rank: number;
}

async function fetchGamificationProfile(
  userId: string,
  organizationId: string,
): Promise<GamificationQueryResult> {
  const { data: authSession } = await supabase.auth.getUser();
  const rawPoints = await GamificationService.getUserPointsForProfile(userId, organizationId);
  const activity = await loadUncreditedAuthorityScore(
    userId,
    organizationId || rawPoints?.organization_id || '',
  );
  const base = normalizeUserPoints(
    rawPoints,
    userId,
    organizationId || rawPoints?.organization_id || '',
  );
  const normalized = base
    ? {
        ...base,
        total_points: base.total_points + activity.points,
        achievement_points: base.achievement_points + activity.points,
        projects_led: Math.max(base.projects_led, activity.projectsLed),
        authority_level: calculateLevelFromPoints(base.total_points + activity.points),
      }
    : activity.points > 0
      ? normalizeUserPoints(
          {
            id: userId,
            user_id: userId,
            organization_id: organizationId,
            total_points: activity.points,
            discipline_points: 0,
            achievement_points: activity.points,
            team_points: 0,
            authority_level: calculateLevelFromPoints(activity.points),
            current_multiplier: 1,
            multiplier_expires_at: null,
            kaizens_completed: 0,
            projects_led: activity.projectsLed,
            team_collaborations: 0,
            recovery_plans_opened: 0,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
          },
          userId,
          organizationId,
        )
      : null;

  const userPerfil = {
    userId,
    organizationId: organizationId || null,
    total_points: normalized?.total_points ?? null,
    authority_level: normalized?.authority_level ?? null,
    hasDbRow: Boolean(rawPoints),
    dbOrganizationId: rawPoints?.organization_id ?? null,
    authMetadata: authSession.user?.user_metadata ?? null,
  };

  if (import.meta.env.DEV) {
    console.log('Dados de Gamificação recuperados:', userPerfil);
  }

  let rank = 0;
  if (normalized && organizationId && organizationId !== 'dev-org') {
    rank = await GamificationService.getUserRank(userId, organizationId);
  }

  return { userPoints: normalized, rank };
}

export const useGamification = () => {
  const { user } = useAuth();
  const { workspace } = useWorkspace();
  const userId = user?.id ?? '';
  const organizationId = workspace?.organizationId ?? '';

  const query = useQuery({
    queryKey: gamificationQueryKey(userId, organizationId),
    enabled: Boolean(userId),
    staleTime: 15_000,
    gcTime: 5 * 60_000,
    meta: { suppressErrors: true },
    queryFn: () => fetchGamificationProfile(userId, organizationId),
  });

  return {
    userPoints: query.data?.userPoints ?? null,
    rank: query.data?.rank ?? 0,
    leaderboard: [] as LeaderboardEntry[],
    loading: query.isPending && !query.data,
    error: query.error ? 'Erro ao carregar dados de gamificação' : null,
    refresh: () => void query.refetch(),
  };
};

export default useGamification;
